/**
 * @file button-action.schema.ts
 * @description Ações possíveis para botões e cards configuráveis.
 *
 * Vive num arquivo próprio (e não dentro de `partner.schema.ts`, de onde veio)
 * porque é usado tanto pelos componentes do Page Builder quanto pelos cards de
 * preparo do agendamento — e `scheduling-common.schema.ts` não pode importar
 * de `partner.schema.ts`, que já importa dele.
 *
 * Ao adicionar um membro novo aqui, três lugares precisam acompanhar:
 *   - `admin/src/widgets/customization/ButtonActionField.tsx` (a UI de config)
 *   - `admin/src/widgets/customization/EditComponentModal.tsx` (isActionComplete)
 *   - `mobile/components/customization/renderers/useButtonAction.ts` (o handler)
 * Os dois últimos são switches exaustivos sem `default`, então o TypeScript
 * aponta o que falta.
 */

import { z } from 'zod';

const UniaoDeAcoes = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('NAVIGATE_TAB'),
    tab: z.enum(['home', 'care', 'content', 'community', 'chat', 'profile']),
  }),
  z.object({
    type: z.literal('OPEN_FORM'),
    formId: z.string(),
  }),
  z.object({
    type: z.literal('EXTERNAL_URL'),
    url: z.string().url(),
  }),
  /**
   * Abre um checkpoint do builder de cuidado no wizard.
   * O item precisa estar publicado — o app devolve "não disponível" caso
   * contrário, em vez de abrir um fluxo vazio.
   */
  z.object({
    type: z.literal('OPEN_CARE_ITEM'),
    careItemId: z.string(),
  }),
  /**
   * Abre uma experiência especial do app (A117), uma tela que não é aba nem
   * formulário. A primeira é "Acenda a sua vela", da jornada Perdi meu bebê
   * — por ora um protótipo navegável, sem nada gravado.
   */
  z.object({
    type: z.literal('OPEN_EXPERIENCE'),
    experience: z.enum(['ACENDER_VELA']),
  }),
  /**
   * Abre a lista completa de um tipo de conteúdo (A123) — o "Ver todos" dos
   * títulos de seção. Revistas seguem a jornada escolhida na tela de
   * Conteúdos; vídeos, o público da paciente, como os carrosséis.
   */
  z.object({
    type: z.literal('OPEN_CONTENT_LIST'),
    /** `PODCAST_E_LIVE` (10/10/2026): podcasts e lives numa lista só, a do "Ver todos" da seção "Podcast e lives". */
    contentType: z.enum(['COURSE', 'DOCUMENTARY', 'SERIES', 'PODCAST', 'LIVE', 'MAGAZINE', 'PODCAST_E_LIVE']),
  }),
  z.object({
    type: z.literal('NONE'),
  }),
]);

/**
 * `OPEN_SCHEDULING` existiu e foi removido quando o agendamento passou a viver
 * dentro da jornada, pelos lembretes. Documento gravado antes disso continua no
 * disco exatamente como estava — o Firestore é schemaless.
 *
 * Sem este preprocess, um único parceiro com um card antigo derruba a LISTA
 * INTEIRA de parceiros: `findMany` lança no primeiro documento inválido, a rota
 * responde erro e o admin mostra uma lista vazia, como se não houvesse
 * parceiro nenhum. Foi exatamente o que aconteceu em QA.
 *
 * Vira `NONE` — o card continua existindo, o botão deixa de prometer uma tela
 * que não abre mais. Reescrever o card é decisão de quem o configurou, e está
 * registrado em `docs/migracoes/`.
 */
function migrarAcaoRemovida(valor: unknown): unknown {
  if (valor && typeof valor === 'object' && (valor as any).type === 'OPEN_SCHEDULING') {
    return { type: 'NONE' };
  }
  return valor;
}

export const ButtonActionSchema = z.preprocess(migrarAcaoRemovida, UniaoDeAcoes);

export type ButtonAction = z.infer<typeof UniaoDeAcoes>;
